import React from 'react';
import { useHistory } from 'react-router-dom';
import {
  Box,
  Stack,
  Paper,
  Typography,
  TextField,
  InputAdornment,
  Button as MuiButton,
  Checkbox,
} from '@mui/material';
import { Search, Filter as FilterIcon, ChevronDown } from 'lucide-react';
import { toast } from '../../../../../ui/toast';
import {
  AppliedFilters,
  Button,
  DataTable,
  Drawer,
} from '../../../../../ui';
import NoDataInList from '../../../../../ui/components/Common/NoData/NoDataInList';
import { ResultsStats } from './Components';
import {
  BehavioralSkillButton,
  TechnicalSkillButton,
} from './Containers/AssessmentResultDetails/Components/Buttons/SkillButtons';
import { FilterPreScreen } from './FilterPreScreen';
import {
  getDynamicBehaviorColumns,
  getDynamicSkillColumns,
} from './Functions/DynamicColumns';
import { getColumns } from './Functions/GetColumns';
import {
  IAssessmentResultsProps,
  useAssessmentResultList,
} from './Hooks/UseAssesmentResultList';

export const AssessmentResults: React.FC<IAssessmentResultsProps> = ({
  assessmentId,
  userResults,
  highestScore,
  completionRatio,
  candidateStatus,
  countTotal,
  countCompleted,
  updateAssessmentUserList,
  preScreenQuestions = [],
  skills = [],
  behaviorSkills = [],
  isAutoAssessment,
  autoAssessmentRoute,
  onResultClick: onResultClickProp,
}) => {
  const history = useHistory();

  const {
    calculateAverageTime,
    resultListData,
    selectedRows,
    setSelectedRows,
    transformUserResultsToApiFormat,
    exportCSV,
    onPageChange,
    handleSortChange,
    hasFetchedOnce,
    pageNumber,
    sort,
    bulkActionOpen,
    setFilters,
    filters,
    applyFilter,
    appliedFilters,
    handleClearFilters,
    handleRemoveFilter,
    selectionDropdownOpen,
    selectedItems,
    setSelectedItems,
    handleSelectAllItems,
    handleToggleSelectionDropdown,
    handleToggleBulkActionDropdown,
    handleBulkMove,
    handleSendUnsuccessfulEmails,
    onResultClick,
    toggleAllSkills,
    toggleSkillVisibility,
    showAllSkills,
    visibleSkills,
    loading,
    serverUserResults,
    serverTotalCount,
    totalPages,
    handleCloseFilter,
    filterOpen,
    handleOpenFilter,
    searchTerm,
    setSearchTerm,
  } = useAssessmentResultList({
    assessmentId,
    userResults,
    highestScore,
    completionRatio,
    candidateStatus,
    countTotal,
    countCompleted,
    updateAssessmentUserList,
    preScreenQuestions,
    skills,
    behaviorSkills,
    onResultClick: onResultClickProp,
  });

  return (
    <Box sx={{ width: '100%', px: { xs: 1, sm: 1.5 } }}>
      <Stack spacing={2}>
        {/* Top bar with stats and search/filter */}
        <Stack
          direction={{ xs: 'column', lg: 'row' }}
          justifyContent="space-between"
          alignItems={{ xs: 'stretch', lg: 'center' }}
          spacing={2}
        >
          <Box sx={{ flex: 1 }}>
            <ResultsStats
              countCompleted={countCompleted}
              countTotal={countTotal}
              calculateAverageTime={calculateAverageTime}
              resultListData={resultListData}
            />
          </Box>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            alignItems={{ xs: 'stretch', sm: 'center' }}
            spacing={1.5}
          >
            <TextField
              size="small"
              placeholder="Search by name or email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search size={16} />
                  </InputAdornment>
                ),
              }}
              sx={{ width: { sm: 260 } }}
            />
            <MuiButton
              variant="outlined"
              size="small"
              startIcon={<FilterIcon size={16} />}
              onClick={handleOpenFilter}
              sx={{ textTransform: 'none', height: 40, borderRadius: 1.5 }}
            >
              Filter
            </MuiButton>
          </Stack>
        </Stack>

        {/* Skills Section */}
        <Paper
          variant="outlined"
          sx={{
            p: 2,
            borderRadius: 2.5,
            bgcolor: 'background.paper',
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
          }}
        >
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
          >
            <Typography variant="subtitle2" fontWeight={700}>
              Skills Overview
            </Typography>
            <MuiButton
              size="small"
              variant="contained"
              color="primary"
              onClick={toggleAllSkills}
              sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 1.5 }}
            >
              {showAllSkills ? 'Hide All Skills' : 'Show All Skills'}
            </MuiButton>
          </Stack>

          <Stack direction={{ xs: 'column', lg: 'row' }} spacing={3}>
            {/* Technical Skills */}
            <Stack direction="row" spacing={1.5} alignItems="flex-start" sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="caption" fontWeight={700} sx={{ pt: 0.5, whiteSpace: 'nowrap' }}>
                Technical:
              </Typography>
              <Stack direction="row" flexWrap="wrap" gap={1}>
                {skills && skills.length > 0 ? (
                  skills.map((skill) => (
                    <TechnicalSkillButton
                      key={skill}
                      label={skill}
                      id={skill}
                      onClick={() => toggleSkillVisibility(skill)}
                      isActive={visibleSkills.has(skill)}
                    />
                  ))
                ) : (
                  <NoDataInList
                    heading="No Technical Skills"
                    description="No technical skills available for this assessment"
                  />
                )}
              </Stack>
            </Stack>

            {/* Behavioral Skills */}
            <Stack direction="row" spacing={1.5} alignItems="flex-start" sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="caption" fontWeight={700} sx={{ pt: 0.5, whiteSpace: 'nowrap' }}>
                Behavioral:
              </Typography>
              <Stack direction="row" flexWrap="wrap" gap={1}>
                {behaviorSkills && behaviorSkills.length > 0 ? (
                  Array.from(new Set(behaviorSkills)).map((behaviorSkill) => (
                    <BehavioralSkillButton
                      key={behaviorSkill}
                      label={behaviorSkill}
                      id={behaviorSkill}
                      onClick={() => toggleSkillVisibility(behaviorSkill)}
                      isActive={visibleSkills.has(behaviorSkill)}
                    />
                  ))
                ) : (
                  <NoDataInList
                    heading="No Behavioral Skills"
                    description="No behavioral skills available for this assessment"
                  />
                )}
              </Stack>
            </Stack>
          </Stack>

          {/* Action Buttons Row */}
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            flexWrap="wrap"
            gap={1.5}
            sx={{ pt: 1, borderTop: '1px solid', borderColor: 'divider' }}
          >
            <Stack direction="row" alignItems="center" spacing={1.5}>
              {isAutoAssessment && (
                <Stack direction="row" alignItems="center" spacing={1.5}>
                  {/* Selection Logic */}
                  <Box sx={{ position: 'relative' }}>
                    <MuiButton
                      variant="outlined"
                      size="small"
                      onClick={handleToggleSelectionDropdown}
                      endIcon={<ChevronDown size={14} />}
                      sx={{ textTransform: 'none', borderRadius: 1.5 }}
                    >
                      <Checkbox
                        size="small"
                        checked={selectedItems.length > 0}
                        sx={{ p: 0, mr: 1 }}
                      />
                      Select
                    </MuiButton>

                    {selectionDropdownOpen && (
                      <Paper
                        elevation={4}
                        sx={{
                          position: 'absolute',
                          top: '100%',
                          left: 0,
                          zIndex: 50,
                          mt: 1,
                          width: 200,
                          borderRadius: 2,
                          py: 1,
                        }}
                      >
                        <Box
                          component="button"
                          onClick={() => handleSelectAllItems('page')}
                          sx={{
                            width: '100%',
                            textAlign: 'left',
                            px: 2,
                            py: 1,
                            fontSize: '0.875rem',
                            border: 'none',
                            bgcolor: 'transparent',
                            cursor: 'pointer',
                            '&:hover': { bgcolor: 'action.hover' },
                          }}
                        >
                          Select Current Page
                        </Box>
                        <Box
                          component="button"
                          onClick={() => handleSelectAllItems('all')}
                          sx={{
                            width: '100%',
                            textAlign: 'left',
                            px: 2,
                            py: 1,
                            fontSize: '0.875rem',
                            border: 'none',
                            bgcolor: 'transparent',
                            cursor: 'pointer',
                            '&:hover': { bgcolor: 'action.hover' },
                          }}
                        >
                          Select All Candidates
                        </Box>
                      </Paper>
                    )}
                  </Box>

                  {/* Bulk Actions */}
                  {selectedItems.length > 0 && (
                    <Box sx={{ position: 'relative' }}>
                      <MuiButton
                        variant="contained"
                        color="primary"
                        size="small"
                        onClick={handleToggleBulkActionDropdown}
                        endIcon={<ChevronDown size={14} />}
                        sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 1.5 }}
                      >
                        Bulk Move ({selectedItems.length})
                      </MuiButton>

                      {bulkActionOpen && (
                        <Paper
                          elevation={4}
                          sx={{
                            position: 'absolute',
                            top: '100%',
                            left: 0,
                            zIndex: 50,
                            mt: 1,
                            width: 220,
                            borderRadius: 2,
                            py: 1,
                          }}
                        >
                          {['Waitlisted', 'Shortlisted', 'Unsuccessful'].map(
                            (option) => (
                              <Box
                                key={option}
                                component="button"
                                onClick={() => handleBulkMove(option)}
                                sx={{
                                  width: '100%',
                                  textAlign: 'left',
                                  px: 2,
                                  py: 1,
                                  fontSize: '0.875rem',
                                  border: 'none',
                                  bgcolor: 'transparent',
                                  display: 'flex',
                                  alignItems: 'center',
                                  cursor: 'pointer',
                                  '&:hover': { bgcolor: 'action.hover' },
                                }}
                              >
                                <Box
                                  sx={{
                                    width: 8,
                                    height: 8,
                                    borderRadius: '50%',
                                    bgcolor: 'primary.main',
                                    mr: 1.5,
                                  }}
                                />
                                Move to {option}
                              </Box>
                            ),
                          )}
                        </Paper>
                      )}
                    </Box>
                  )}
                </Stack>
              )}
            </Stack>

            <Stack direction="row" alignItems="center" spacing={1.5}>
              {isAutoAssessment && selectedItems.length > 0 && (
                <Button
                  variant="secondary"
                  onClick={handleSendUnsuccessfulEmails}
                  label="Send Unsuccessful Email(s)"
                />
              )}
              <Button
                variant="primary"
                onClick={() => {
                  if (selectedRows.length > 0) {
                    toast.success('Selected candidates marked as checked', {
                      position: 'bottom-center',
                    });
                    setSelectedRows([]);
                  }
                }}
                disabled={selectedRows.length === 0}
                label="Mark As Checked"
                sx={{ height: 32, px: 2, fontSize: '0.75rem' }}
              />
              <Button
                variant="primary"
                onClick={() => exportCSV()}
                label="Export Candidate CSV"
                sx={{ height: 32, px: 2, fontSize: '0.75rem' }}
              />
            </Stack>
          </Stack>
        </Paper>

        {Object.keys(appliedFilters).length > 0 && (
          <AppliedFilters
            appliedFilters={appliedFilters}
            handleClearFilters={handleClearFilters}
            handleRemoveFilter={handleRemoveFilter}
          />
        )}

        <Box sx={{ width: '100%', minHeight: 360, height: 'calc(100vh - 280px)', minWidth: 0 }}>
          {(() => {
            const displayData =
              transformUserResultsToApiFormat(serverUserResults);

            return (
              <DataTable
                hasCheckBox
                isLoading={loading || !hasFetchedOnce}
                columns={(() => {
                  const baseColumns = getColumns(
                    skills && skills.length > 0,
                    behaviorSkills && behaviorSkills.length > 0,
                  );

                  const visibleTechnicalSkills = new Set(
                    Array.from(visibleSkills).filter((s) =>
                      skills.includes(s),
                    ),
                  );
                  const visibleBehaviorSkills = new Set(
                    Array.from(visibleSkills).filter((s) =>
                      behaviorSkills.includes(s),
                    ),
                  );

                  const dynamicSkills = getDynamicSkillColumns(
                    visibleTechnicalSkills,
                  );
                  const dynamicBehaviors = getDynamicBehaviorColumns(
                    visibleBehaviorSkills,
                  );

                  const insertIndex = 4;
                  return [
                    ...baseColumns.slice(0, insertIndex),
                    ...dynamicSkills,
                    ...dynamicBehaviors,
                    ...baseColumns.slice(insertIndex),
                  ];
                })()}
                data={displayData}
                rowsPerPage={20}
                onPageChange={onPageChange}
                currentPage={pageNumber}
                totalPages={totalPages}
                onSortChange={handleSortChange}
                sortConfig={Object.entries(sort).map(([key, direction]) => ({
                  key,
                  direction,
                }))}
                totalRecords={serverTotalCount}
                onClickRow={(assessmentResult) => {
                  if (onResultClick) {
                    onResultClick(assessmentResult);
                  } else {
                    history.push(
                      isAutoAssessment
                        ? `${autoAssessmentRoute}/${assessmentResult.assessmentId}/${assessmentResult.userId}/${assessmentResult.id}`
                        : `/view-assessment-result/${assessmentResult.assessmentId}/${assessmentResult.userId}/${assessmentResult.id}`,
                    );
                  }
                }}
                onSelectAll={() => {
                  if (
                    displayData.every((row) =>
                      selectedItems.includes(row.userId),
                    )
                  ) {
                    setSelectedItems(
                      selectedItems.filter(
                        (id) => !displayData.some((row) => row.userId === id),
                      ),
                    );
                  } else {
                    const newSelectedItems = new Set(selectedItems);
                    displayData.forEach((row) =>
                      newSelectedItems.add(row.userId),
                    );
                    setSelectedItems(Array.from(newSelectedItems));
                  }
                }}
                onSelectRow={(_index, row) => {
                  if (selectedItems.includes(row.userId)) {
                    setSelectedItems(
                      selectedItems.filter((id) => id !== row.userId),
                    );
                  } else {
                    setSelectedItems([...selectedItems, row.userId]);
                  }
                }}
                isRowSelected={(_index, row) =>
                  selectedItems.includes(row.userId)
                }
                isAllSelected={
                  displayData.length > 0 &&
                  displayData.every((row) =>
                    selectedItems.includes(row.userId),
                  )
                }
                isIndeterminate={
                  displayData.some((row) =>
                    selectedItems.includes(row.userId),
                  ) &&
                  !displayData.every((row) =>
                    selectedItems.includes(row.userId),
                  )
                }
              />
            );
          })()}
        </Box>
      </Stack>

      <Drawer
        isOpen={filterOpen}
        onClose={handleCloseFilter}
        footerContent={
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            sx={{
              p: 2,
              borderTop: '1px solid',
              borderColor: 'divider',
              bgcolor: 'action.hover',
            }}
          >
            <Button
              label="Cancel"
              variant="secondary"
              onClick={handleCloseFilter}
            />
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Button
                label="Reset"
                variant="link"
                onClick={handleClearFilters}
              />
              <Button
                label="Apply Filters"
                onClick={applyFilter}
              />
            </Stack>
          </Stack>
        }
        title="Filters"
        width="700px"
      >
        <FilterPreScreen
          questions={preScreenQuestions}
          setFilters={setFilters}
          filters={filters}
        />
      </Drawer>
    </Box>
  );
};
